import { convexQuery } from '@convex-dev/react-query';
import { api } from '@repo/backend/_generated/api';
import { ROLES } from '@repo/backend/schema';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

export type ViewAsMode = 'default' | 'brand';

const STORAGE_KEY = 'promote:view-as-mode';

function isViewAsMode(value: unknown): value is ViewAsMode {
    return value === 'default' || value === 'brand';
}

function getSnapshot(): ViewAsMode {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isViewAsMode(raw) ? raw : 'default';
}

function getServerSnapshot(): ViewAsMode {
    return 'default';
}

let listeners: Array<() => void> = [];

function subscribe(listener: () => void) {
    listeners = [...listeners, listener];
    const onStorage = (e: StorageEvent) => {
        if (e.key === STORAGE_KEY) listener();
    };
    window.addEventListener('storage', onStorage);
    return () => {
        listeners = listeners.filter((l) => l !== listener);
        window.removeEventListener('storage', onStorage);
    };
}

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

/**
 * Local-only "view as" override for super users to QA role-gated UI.
 *
 * - Affects only frontend role checks; backend always sees the real user.
 * - Can only suppress UI gates, never grant new privileges.
 */
export function useViewAsMode(): { mode: ViewAsMode; setMode: (mode: ViewAsMode) => void } {
    const mode = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

    const setMode = useCallback((next: ViewAsMode) => {
        if (next === 'default') {
            localStorage.removeItem(STORAGE_KEY);
        } else {
            localStorage.setItem(STORAGE_KEY, next);
        }
        emitChange();
    }, []);

    return { mode, setMode };
}

const ELEVATED_ROLES = [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.MODERATOR] as const;

/**
 * Returns whether the current user should be treated as having an elevated
 * role (super_user / admin / moderator) for UI gating purposes. Honors the
 * "view as" override from `useViewAsMode` — when set to 'brand', returns
 * false even for actual super users.
 */
export function useHasElevatedRole(): boolean {
    const { data: currentUser } = useQuery(convexQuery(api.users.current, {}));
    const { mode } = useViewAsMode();

    if (mode === 'brand') return false;
    return currentUser?.roles?.some((role) => (ELEVATED_ROLES as readonly string[]).includes(role)) ?? false;
}
